import React from "react";
import { labels } from "../../../../../../localization/labels";
import { messages } from "../../../../../../localization/messages";
import { activeFilterPageInfo } from "../../../../../../services/FilteringUtils";
import InvolvementRoleTypeService from "../../../../../../services/InvolvementRoleTypeService";
import PartyRoleTypeDiscriminatorService from "../../../../../../services/PartyRoleTypeDiscriminatorService";
import PartyRoleTypeService from "../../../../../../services/PartyRoleTypeService";
import CommonUtil from "../../../../../../utils/CommonUtil";
import PathUtil from "../../../../../../utils/PathUtil";
import {
  rolesValidationRules as validationRules,
  validateEntity
} from "../../../../../../validators/validationRules";
import { paths } from "../../../../../paths";
import SubControlComponent from "../../../../page/SubControlComponent/SubControlComponent";
import RoleTypeDetail from "./RoleTypeDetail";

class RoleTypeDetailContainer extends SubControlComponent {
  constructor(props) {
    super(props);

    this.partyRoleTypeDiscriminatorService = new PartyRoleTypeDiscriminatorService();
    this.involvementRoleTypeService = new InvolvementRoleTypeService();
    this.partyRoleTypeService = new PartyRoleTypeService();

    this.maxLength = {
      name: 50,
      description: 2000
    };

    this.sortInfo = {
      orderBy: "name",
      direction: "asc"
    };

    this.state = {
      id: this.resolveItemId()
    };
    this.state = this.initialState(this.state);
  }

  initialState = state => {
    return {
      ...state,
      disabled: true,
      entity: {
        name: undefined,
        description: undefined,
        dateFromTo: {
          dateFrom: undefined,
          dateTo: undefined
        },
        partyRoleTypeDiscriminator: undefined,
        involvementRoleTypes: [],
        parent: undefined
      },
      possibleParents: [],
      discriminators: []
    };
  };

  componentDidUpdate(prevProps) {
    const newId = this.resolveItemId();
    if (newId !== PathUtil.pathParameter(prevProps, "id")) {
      this.state.id = newId;
      this.setState(this.initialState(this.state));
      this.loadData();
    }
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    if (this.state.id) {
      this.loadSelectedEntity(this.state.id);
    } else {
      this.setComponentTitle(labels.roleType);
      this.loadPossibleParents();
    }

    this.loadAllDiscriminators();
  };

  getComponentTitle = () => {
    return this.state.entity ? this.state.entity.name : undefined;
  };

  onEntityLoad = data => {
    this.setState({
      disabled: false,
      entity: data
    });
    this.setComponentTitle(this.state.entity.name, true);
    this.loadParentInvolvementRoleTypes(
      data._links.parentInvolvementRoleTypes.href
    );
    this.loadEntityDiscriminator(data._links.discriminator.href);
    this.loadEntityParent(data._links.parent.href);
    this.loadPossibleParents();
  };

  loadPossibleParents = () => {
    this.prepareCall(
      this.partyRoleTypeService.getEntities(
        activeFilterPageInfo,
        this.sortInfo,
        this.onPossibleParentsLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onPossibleParentsLoad = data => {
    let possibleParents = [];
    if (data && data._embedded && data._embedded.content) {
      possibleParents = data._embedded.content.filter(
        item => item.id !== this.state.entity.id
      );
    }
    this.setState({
      possibleParents: possibleParents
    });
  };

  loadEntityParent = url => {
    this.prepareCall(
      this.partyRoleTypeService.getEntitiesWithParams(
        url,
        activeFilterPageInfo,
        undefined,
        this.onParentLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onParentLoad = data => {
    if (
      data &&
      data._embedded &&
      data._embedded.content &&
      data._embedded.content.length
    ) {
      const { entity } = this.state;
      entity.parent = data._embedded.content[0];
      this.setState({ entity });
    }
  };

  loadEntityInvolvementTypes = url => {
    this.prepareCall(
      this.involvementRoleTypeService.getEntitiesWithParams(
        url,
        activeFilterPageInfo,
        undefined,
        this.onEntityInvolvementTypesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadParentInvolvementRoleTypes = url => {
    this.prepareCall(
      this.involvementRoleTypeService.getEntitiesWithParams(
        url,
        activeFilterPageInfo,
        undefined,
        this.onParentInvolvementRoleTypesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onParentInvolvementRoleTypesLoad = data => {
    const { entity } = this.state;
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      entity.parentInvolvementRoleTypes = dataContent;
    } else {
      entity.parentInvolvementRoleTypes = [];
    }
    this.setState({ entity });
    this.loadEntityInvolvementTypes(
      this.state.entity._links.involvementRoleTypes.href
    );
  };

  onEntityInvolvementTypesLoad = data => {
    const { entity } = this.state;
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      entity.involvementRoleTypes = dataContent;
    } else {
      entity.involvementRoleTypes = [];
    }
    this.setState({ entity });
  };

  loadSelectedEntity = id => {
    this.prepareCall(
      this.partyRoleTypeService.getPartyRoleTypeById(
        id,
        this.onEntityLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  handleOnDelete = () => {
    const confirmed = window.confirm(messages.delete_selected_confirm_message);
    if (confirmed) {
      this.onDeleteData(this.state.entity);
    }
  };

  onDeleteLoad = () => {
    const path = PathUtil.adminPath(paths.ROLES);
    this.changePath(path);
  };

  onDeleteData = selected => {
    this.prepareCall(
      this.partyRoleTypeService.deleteEntity(
        selected,
        this.onDeleteLoad,
        this.onDeleteSuccessMessage,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  handleDialogChange = event => {
    let { entity } = this.state;
    const path = event.target.id;
    CommonUtil.setDeepOnStringPath(entity, path, event.target.value);
    const disabled = !this.validator(entity);
    this.setState({ entity, disabled });
  };

  validator = entity => {
    return validateEntity(entity, validationRules.roleTypeDetails);
  };

  onEntitySave = () => {
    const entity = {
      ...this.state.entity
    };

    if (!this.state.id) {
      this.saveNewEntity(entity);
    } else {
      this.updateEntity(entity);
    }
  };

  onSaveLoad = data => {
    if (this.state.id) {
      this.loadSelectedEntity(this.state.id);
    } else {
      const itemPath = PathUtil.adminItemsPath(
        { path: paths.ROLES },
        { path: paths.DETAIL, id: data.id }
      );
      this.changePath(itemPath, data.name);
    }
  };

  saveNewEntity = entity => {
    this.prepareCall(
      this.partyRoleTypeService.postEntity(
        entity,
        this.onSaveLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  updateEntity = entity => {
    this.prepareCall(
      this.partyRoleTypeService.putEntity(
        entity,
        this.onSaveLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onDiscriminatorLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      const { entity } = this.state;
      entity.partyRoleTypeDiscriminator = dataContent[0];
      this.setState({ entity });
    }
  };

  loadEntityDiscriminator = url => {
    this.prepareCall(
      this.partyRoleTypeDiscriminatorService.getEntitiesByUrl(
        url,
        this.onDiscriminatorLoad,
        this.messageHandler.handleError
      )
    );
  };

  onDiscriminatorsLoad = data => {
    if (data && data._embedded && data._embedded.content) {
      this.setState({ discriminators: data._embedded.content });
    }
  };

  loadAllDiscriminators = () => {
    this.prepareCall(
      this.partyRoleTypeDiscriminatorService.getEntities(
        undefined,
        this.sortInfo,
        this.onDiscriminatorsLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  getSubArrayPath = () => {
    return "involvementRoleTypes";
  };

  render() {
    return (
      <RoleTypeDetail
        {...this.renderProps()}
        handleOnDelete={this.handleOnDelete}
        onChange={this.handleDialogChange}
        maxLength={this.maxLength}
        onSave={this.onEntitySave}
        onDelete={this.handleOnDelete}
        disabled={this.state.disabled}
        isNew={this.state.id ? false : true}
        validationRules={validationRules.roleTypeDetails}
        {...this.handlerProps()}
        discriminators={this.state.discriminators}
        possibleParents={this.state.possibleParents}
      />
    );
  }
}

export default RoleTypeDetailContainer;
